import { useEffect, useLayoutEffect, useRef } from 'react';
import { Copy, Filter, FilterX, Plus } from 'lucide-react';

export type TokenField = 'tag' | 'pid' | 'level';

/**
 * Plain-object subset of `DOMRect` we hold on to. We must NOT keep the
 * original anchor element: in the virtualized log list react-window
 * recycles row DOM on each tick, so by the time the menu mounts the
 * element may be detached → `getBoundingClientRect()` returns zero-rect
 * and the popover ends up at (0,0).
 */
export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * Popover action 语义：
 *   - `'only'`：把当前 token 替换成"只看此值"——pid / level 走这条
 *     （保留旧行为，只看此 PID / 只看此 Level）；
 *   - `'include'`：把 value 加进 `tag:` 包含集合（多值用 `|` 拼接，已存在
 *     则移除）。tag 字段下取代 `'only'` 作为左键单击的右键二级入口；
 *   - `'exclude'`：tag 走 `-tag:` 的同款 toggle；pid / level 仍是单值排
 *     除（追加 `-pid:` / `-level:`，不去 toggle）；
 *   - `'copy'`：navigator.clipboard 复制原始值。
 */
export type TokenMenuAction = 'only' | 'exclude' | 'include' | 'copy';

interface Props {
  field: TokenField;
  value: string;
  anchor: AnchorRect;
  onPick: (action: TokenMenuAction) => void;
  onClose: () => void;
}

const MENU_WIDTH = 208; // matches the w-52 tailwind class below
const MENU_HEIGHT_APPROX = 160;

/**
 * Tiny popover triggered by clicking a tag / pid / level token in a log
 * row. Pinned directly below the clicked token (or above, if no room),
 * with a green accent border so the action target is unmistakable.
 */
export function TokenMenu({ field, value, anchor, onPick, onClose }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.style.position = 'fixed';
    // Default below; flip above if it would overflow the viewport.
    const wouldOverflowBottom = anchor.bottom + 4 + MENU_HEIGHT_APPROX > window.innerHeight - 8;
    const top = wouldOverflowBottom
      ? Math.max(8, anchor.top - 4 - MENU_HEIGHT_APPROX)
      : anchor.bottom + 4;
    const left = Math.max(8, Math.min(window.innerWidth - MENU_WIDTH - 8, anchor.left));
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [anchor]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const label = field === 'tag' ? '标签' : field === 'pid' ? 'PID' : 'Level';
  const isTag = field === 'tag';

  return (
    <div
      ref={wrapRef}
      // Inline `zIndex` instead of the `z-50` tailwind class on purpose:
      // the menu uses `position: fixed` but lives inside a sibling tree
      // that contains LogList's floating ChevronsUp/Down buttons. Those
      // sit inside an `absolute` stacking context with `z-10`; depending
      // on whether ancestors create their own contexts (overflow:hidden,
      // transforms, opacity), a tailwind `z-50` *can* still lose to a
      // sibling `z-10`. A four-digit value defeats every plausible
      // stacking-context fight without affecting non-portaled siblings.
      style={{ zIndex: 1000 }}
      className="w-52 overflow-hidden rounded-md border-2 border-emerald-500 bg-bg-card p-0 text-xs shadow-lg shadow-emerald-500/20 ring-1 ring-emerald-500/30"
      role="menu"
    >
      <div className="flex items-stretch border-b border-line">
        <div className="w-1 bg-emerald-500" aria-hidden />
        <div className="flex-1 px-2 py-1 text-[10px] text-ink-muted">
          <span className="font-medium text-emerald-600 dark:text-emerald-400">{label}</span>:{' '}
          <code className="font-mono text-ink">{value}</code>
        </div>
      </div>
      <div className="p-1">
        {isTag ? (
          <>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-emerald-500/10"
              onClick={() => onPick('include')}
            >
              <Plus className="h-3.5 w-3.5 text-emerald-500" />
              <span>加到包含</span>
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-bg-soft"
              onClick={() => onPick('exclude')}
            >
              <FilterX className="h-3.5 w-3.5 text-rose-500" />
              <span>加到排除</span>
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-emerald-500/10"
              onClick={() => onPick('only')}
            >
              <Filter className="h-3.5 w-3.5 text-emerald-500" />
              <span>只看此{label}</span>
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-bg-soft"
              onClick={() => onPick('exclude')}
            >
              <FilterX className="h-3.5 w-3.5 text-rose-500" />
              <span>排除此{label}</span>
            </button>
          </>
        )}
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-bg-soft"
          onClick={() => onPick('copy')}
        >
          <Copy className="h-3.5 w-3.5 text-ink-muted" />
          <span>复制值</span>
        </button>
      </div>
    </div>
  );
}
